import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { motion, AnimatePresence } from 'framer-motion';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const { LuCar, LuEye, LuTrendingUp, LuCheckCircle, LuSlidersHorizontal, LuX, LuChevronDown, LuChevronUp, LuMegaphone, LuStar, LuShare2, LuPlayCircle, LuVideo, LuExternalLink, LuQuote, LuMessageSquareHeart } = LuIcons;

// المشغل الذكي
export const SmartVideoPlayer = ({ url, title, className = "absolute inset-0 w-full h-full" }) => {
  if (!url) return null;

  if (url.includes('<iframe')) {
    return <div className={className} dangerouslySetInnerHTML={{ __html: url }} />;
  }

  if (url.includes('tiktok.com')) {
    return (
      <div className={`${className} flex flex-col items-center justify-center bg-black/90 p-4 z-10`}>
        <p className="text-white font-bold mb-4 text-center">فيديو تيك توك</p>
        <a href={url} target="_blank" rel="noreferrer" className="bg-rose-500 text-white px-6 py-2 rounded-xl font-bold flex items-center gap-2 hover:bg-rose-600 transition-colors">
          <SafeIcon icon={LuPlayCircle} className="w-5 h-5" /> شاهد على تيك توك
        </a>
      </div>
    );
  }

  let embedUrl = url;
  if (url.includes('youtube.com/shorts/')) {
    embedUrl = url.replace('youtube.com/shorts/', 'youtube.com/embed/').split('?')[0];
  } else if (url.includes('watch?v=')) {
    try {
      const videoId = new URL(url).searchParams.get('v');
      if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
    } catch(e) {}
  } else if (url.includes('youtu.be/')) {
    const videoId = url.split('youtu.be/')[1].split('?')[0];
    if (videoId) embedUrl = `https://www.youtube.com/embed/${videoId}`;
  }

  const isDirectVideo = url.match(/\.(mp4|webm|ogg)$/i);
  if (isDirectVideo) {
    return <video src={url} controls className={`${className} object-cover`}></video>;
  }

  return (
    <iframe 
      src={embedUrl}
      className={`${className} border-0`}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
      title={title || "Video Player"}
    ></iframe>
  );
};

// 1. StatsBar Component
const StatItem = ({ icon, value, label, color }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3">
    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`}>
      <SafeIcon icon={icon} className="w-4 h-4 text-white" />
    </div>
    <div>
      <p className="text-lg font-black text-slate-900 dark:text-white leading-none">{value}</p>
      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{label}</p>
    </div>
  </motion.div>
);

export const StatsBar = () => {
  const [stats, setStats] = useState({ total: 0, available: 0, sold: 0, views: 0 });
  
  useEffect(() => {
    const fetchStats = async () => {
      const { data } = await supabase.from('cars_for_sale').select('status, views_count');
      if (data) {
        setStats({
          total: data.length,
          available: data.filter(c => c.status !== 'sold').length,
          sold: data.filter(c => c.status === 'sold').length,
          views: data.reduce((s, c) => s + (c.views_count || 0), 0),
        });
      }
    };
    fetchStats();
  }, []);

  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-2xl shadow-sm px-6 py-4 mb-8">
      <div className="flex flex-wrap gap-6 justify-around">
        <StatItem icon={LuCar} value={stats.total} label="إجمالي الإعلانات" color="bg-blue-500" />
        <div className="w-px bg-slate-100 dark:bg-slate-700 hidden sm:block" />
        <StatItem icon={LuTrendingUp} value={stats.available} label="متوفرة للبيع" color="bg-emerald-500" />
        <div className="w-px bg-slate-100 dark:bg-slate-700 hidden sm:block" />
        <StatItem icon={LuCheckCircle} value={stats.sold} label="تم بيعها" color="bg-red-400" />
        <div className="w-px bg-slate-100 dark:bg-slate-700 hidden sm:block" />
        <StatItem icon={LuEye} value={stats.views.toLocaleString()} label="إجمالي المشاهدات" color="bg-violet-500" />
      </div>
    </div>
  );
};

// 2. CarFilters Component
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 20 }, (_, i) => currentYear - i);

export const CarFilters = ({ filters, onChange, onReset, hasActiveFilters }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="w-full">
      <button 
        onClick={() => setOpen(o => !o)} 
        className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border-2 font-bold text-sm transition-all ${hasActiveFilters ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
      >
        <SafeIcon icon={LuSlidersHorizontal} className="w-4 h-4" /> 
        فلترة متقدمة
        {hasActiveFilters && <span className="bg-emerald-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">!</span>}
        <SafeIcon icon={open ? LuChevronUp : LuChevronDown} className="w-3.5 h-3.5 ms-auto" />
      </button>

      {open && (
        <div className="mt-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-lg p-5 space-y-5">
          <div className="flex justify-between items-center">
            <h3 className="font-bold text-slate-800 dark:text-white">خيارات البحث</h3>
            {hasActiveFilters && (
              <button onClick={onReset} className="text-xs text-red-500 hover:text-red-700 flex items-center gap-1 font-medium"><SafeIcon icon={LuX} className="w-3 h-3" /> مسح الكل</button>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">ناقل الحركة</label>
            <div className="flex gap-2">
              {[{val:'', label:'الكل'}, {val:'automatic', label:'أوتوماتيك'}, {val:'manual', label:'يدوي'}].map(opt => (
                <button 
                  key={opt.val} 
                  onClick={() => onChange('transmission', opt.val)}
                  className={`flex-1 py-2 px-3 rounded-xl text-sm font-bold border-2 transition-all ${filters.transmission === opt.val ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400' : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">السنة</label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <select value={filters.yearFrom} onChange={e => onChange('yearFrom', e.target.value)} className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white">
                  <option value="">من سنة (الكل)</option>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <select value={filters.yearTo} onChange={e => onChange('yearTo', e.target.value)} className="w-full p-2.5 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-sm focus:ring-2 focus:ring-emerald-500 outline-none dark:text-white">
                  <option value="">إلى سنة (الكل)</option>
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase mb-2">ترتيب العرض</label>
            <div className="grid grid-cols-2 gap-2">
              {[
                {val: 'newest', label: '🆕 الأحدث أولاً'},
                {val: 'oldest', label: '🕰️ الأقدم أولاً'},
                {val: 'price_asc', label: '💰 السعر: الأقل'},
                {val: 'price_desc', label: '💎 السعر: الأعلى'}
              ].map(opt => (
                <button 
                  key={opt.val} 
                  onClick={() => onChange('sort', opt.val)}
                  className={`py-2 px-3 rounded-xl text-xs font-bold border-2 transition-all text-start ${filters.sort === opt.val ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400' : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700'}`}
                >{opt.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 3. TextAdBanner Component
const FONT_SIZE_MAP = {
  sm: '14px',
  base: '16px',
  lg: '18px',
  xl: '20px',
  '2xl': '24px'
};

export const TextAdBanner = ({ ads }) => {
  if (!ads || ads.length === 0) return null;
  const sortedAds = [...ads].sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div className="w-full space-y-3 my-6">
      {sortedAds.map(ad => {
        let animationClass = '';
        if (ad.is_marquee) {
          animationClass = ad.marquee_direction === 'ltr' ? 'animate-marquee-ltr' : 'animate-marquee-rtl';
        }
        return (
          <div key={ad.id} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 rounded-2xl shadow-md overflow-hidden">
            <div className="flex items-center px-5 py-4 gap-4">
              <div className="flex-shrink-0 w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center z-10">
                <SafeIcon icon={LuMegaphone} className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 marquee-container relative flex flex-col justify-center">
                <div style={{ textAlign: ad.is_marquee ? 'start' : (ad.text_align || 'right'), width: '100%' }}>
                  <p 
                    className={`text-white font-bold leading-relaxed ${animationClass}`}
                    style={{ 
                      fontSize: FONT_SIZE_MAP[ad.font_size] || '16px',
                      animationDuration: ad.is_marquee ? `${ad.marquee_speed || 15}s` : 'unset',
                      margin: 0
                    }}
                  >
                    {ad.content}
                  </p>
                </div>
              </div>
              <div className="flex-shrink-0 z-10"><SafeIcon icon={LuStar} className="w-5 h-5 text-yellow-300" /></div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 3.1 TopBarTextAd Component
export const TopBarTextAd = ({ ads }) => {
  const textAds = ads.filter(ad => ad.location === 'top_bar' && ad.type === 'text' && ad.is_active).sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0));
  if (textAds.length === 0) return null;

  return (
    <div className="w-full flex flex-col">
      {textAds.map(ad => {
        let animationClass = '';
        if (ad.is_marquee) {
          animationClass = ad.marquee_direction === 'ltr' ? 'animate-marquee-ltr' : 'animate-marquee-rtl';
        }
        return (
          <div key={ad.id} className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 text-white py-2.5 border-b border-emerald-700/50">
            <div className="max-w-7xl mx-auto px-4 w-full marquee-container">
              <div style={{ textAlign: ad.is_marquee ? 'start' : (ad.text_align || 'center'), width: '100%' }}>
                <p 
                  className={`font-semibold tracking-wide ${animationClass}`}
                  dir="rtl"
                  style={{ 
                    fontFamily: "'Cairo', sans-serif",
                    fontSize: FONT_SIZE_MAP[ad.font_size] || '14px',
                    animationDuration: ad.is_marquee ? `${ad.marquee_speed || 15}s` : 'unset',
                    margin: 0
                  }}
                >
                  {ad.content}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// 3.2 TopBarImageAd Component
export const TopBarImageAd = ({ ads }) => {
  const imgAds = ads.filter(ad => ad.location === 'top_bar' && ad.type === 'image' && ad.is_active).sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0));
  if (imgAds.length === 0) return null;

  return (
    <div className="w-full">
      {imgAds.map(ad => (
        <div key={ad.id} className="w-full overflow-hidden">
          {ad.target_url ? 
            <a href={ad.target_url} target="_blank" rel="noreferrer">
              <img src={ad.content} alt={ad.title} className="w-full h-auto block" />
            </a> : 
            <img src={ad.content} alt={ad.title} className="w-full h-auto block" />
          }
        </div>
      ))}
    </div>
  );
};

// 3.5 ImageAdBanner with Slider Support
export const ImageAdBanner = ({ ad }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = ad.images && ad.images.length > 0 ? ad.images : [ad.content];

  useEffect(() => {
    if (images.length > 1) {
      const timer = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % images.length);
      }, 4000); // تغيير الصورة كل 4 ثواني
      return () => clearInterval(timer);
    }
  }, [images.length]);

  const inner = (
    <div className="relative w-full aspect-[21/9] sm:aspect-[32/9] bg-slate-100 overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.img 
          key={currentIndex}
          src={images[currentIndex]} 
          alt={ad.title || 'إعلان'} 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="absolute inset-0 w-full h-full object-cover" 
          onError={e => e.target.style.display = 'none'}
        />
      </AnimatePresence>
      {/* مؤشرات السلايدر */}
      {images.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {images.map((_, idx) => (
            <button 
              key={idx} 
              onClick={(e) => { e.preventDefault(); setCurrentIndex(idx); }}
              className={`h-1.5 rounded-full transition-all ${idx === currentIndex ? 'bg-white w-6' : 'bg-white/50 w-2'}`}
            />
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="w-full rounded-2xl overflow-hidden shadow-sm my-4 border border-slate-100 dark:border-slate-700">
      {ad.title && <h3 className="bg-slate-900 dark:bg-slate-950 text-white font-bold text-center py-2 text-sm">{ad.title}</h3>}
      {ad.target_url ? <a href={ad.target_url} target="_blank" rel="noreferrer" className="block">{inner}</a> : inner}
    </div>
  );
};

// 4. AdRenderer
export const AdRenderer = ({ ads, location }) => {
  const locationAds = ads.filter(ad => ad.location === location && ad.is_active).sort((a,b) => (a.sort_order || 0) - (b.sort_order || 0));
  if (locationAds.length === 0) return null;

  return (
    <div className="w-full space-y-4 my-6">
      {locationAds.filter(ad => ad.type === 'text').length > 0 && (
        <TextAdBanner ads={locationAds.filter(ad => ad.type === 'text')} />
      )}
      {locationAds.filter(ad => ad.type === 'image').map(ad => (
        <ImageAdBanner key={ad.id} ad={ad} />
      ))}
      {locationAds.filter(ad => ad.type === 'code').map(ad => (
        <div key={ad.id} className="w-full flex justify-center overflow-hidden bg-white dark:bg-slate-800 rounded-2xl p-2" dangerouslySetInnerHTML={{ __html: ad.content }} />
      ))}
    </div>
  );
};

export const TopBarSocial = () => {
  const [links, setLinks] = useState([]);

  useEffect(() => {
    supabase.from('social_links').select('*').eq('is_active', true).order('sort_order').then(({ data }) => {
      if (data) setLinks(data);
    });
  }, []);

  if (links.length === 0) return null;

  return (
    <div className="flex items-center gap-2">
      {links.map(link => (
        <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-emerald-900 text-slate-500 dark:text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400 flex items-center justify-center transition-colors">
          {link.custom_image ? <img src={link.custom_image} alt={link.platform} className="w-4 h-4 object-contain" /> : <SafeIcon icon={LuIcons[link.icon]} className="w-4 h-4" />}
        </a>
      ))}
    </div>
  );
};

export const TelegramBanner = () => {
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem('tg_banner_dismissed') === 'true');
  const [links, setLinks] = useState([]);

  useEffect(() => {
    supabase.from('social_links').select('*').eq('is_active', true).order('sort_order').then(({ data }) => {
      if (data) setLinks(data);
    });
  }, []);

  if (dismissed || links.length === 0) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="w-full bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <div className="flex justify-between items-center text-white/90 text-xs">
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-bold text-emerald-400">تابعنا على قنواتنا:</span>
              <div className="flex items-center gap-3">
                {links.map(link => (
                  <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-white transition-colors" title={link.platform}>
                    {link.custom_image ? <img src={link.custom_image} alt={link.platform} className="w-3.5 h-3.5 object-contain" /> : <SafeIcon icon={LuIcons[link.icon]} className="w-3.5 h-3.5" />}
                    <span className="hidden sm:inline">{link.platform}</span>
                  </a>
                ))}
              </div>
            </div>
            <button onClick={() => { sessionStorage.setItem('tg_banner_dismissed', 'true'); setDismissed(true); }} className="text-white/50 hover:text-white p-1 rounded-md transition-colors">
              <SafeIcon icon={LuX} className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export const FloatingSocial = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [links, setLinks] = useState([]);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 2000);
    supabase.from('social_links').select('*').eq('is_active', true).order('sort_order').then(({ data }) => {
      if (data) setLinks(data);
    });
    return () => clearTimeout(timer);
  }, []);

  if (!visible || links.length === 0) return null;

  return (
    <div className="fixed bottom-6 left-6 z-40 flex flex-col items-start gap-3">
      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ opacity: 0, y: 20, scale: 0.8 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.8 }} className="flex flex-col gap-2">
            {links.map(link => (
              <a key={link.id} href={link.url} target="_blank" rel="noreferrer" className="flex items-center gap-3 bg-slate-900 dark:bg-slate-800 text-white px-4 py-2.5 rounded-2xl shadow-lg hover:bg-slate-800 dark:hover:bg-slate-700 transition-colors group">
                {link.custom_image ? <img src={link.custom_image} alt={link.platform} className="w-5 h-5 flex-shrink-0 object-contain" /> : <SafeIcon icon={LuIcons[link.icon]} className="w-5 h-5 flex-shrink-0" />}
                <span className="text-sm font-bold w-20">{link.platform}</span>
              </a>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      <button onClick={() => setExpanded(!expanded)} className={`relative flex items-center gap-2 px-5 h-[52px] rounded-2xl text-white font-black shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 ${expanded ? 'bg-slate-800' : 'bg-gradient-to-br from-emerald-500 to-teal-600'}`}>
        <SafeIcon icon={expanded ? LuX : LuShare2} className="w-5 h-5" />
        <span>{expanded ? 'إغلاق' : 'تابعنا'}</span>
        {!expanded && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-red-500 rounded-full animate-pulse border-2 border-white"></span>}
      </button>
    </div>
  );
};

export const HomeReviews = ({ location = 'home_above_cars' }) => {
  const [groupedReviews, setGroupedReviews] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.from('customer_reviews')
      .select('*')
      .eq('is_active', true)
      .eq('location', location)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data && data.length > 0) {
          const groups = data.reduce((acc, review) => {
            const title = review.section_title || 'آراء زبائننا';
            if (!acc[title]) acc[title] = [];
            acc[title].push(review);
            return acc;
          }, {});
          setGroupedReviews(groups);
        }
        setLoading(false);
      });
  }, [location]);

  if (loading || Object.keys(groupedReviews).length === 0) return null;

  return (
    <div className="w-full my-8 space-y-10">
      {Object.entries(groupedReviews).map(([groupTitle, reviews]) => (
        <div key={groupTitle} className="w-full">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl sm:text-2xl font-black text-slate-800 dark:text-white flex items-center gap-2">
              <SafeIcon icon={LuMessageSquareHeart} className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />
              {groupTitle}
            </h3>
            <a href="/reviews" className="text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/30 px-3 py-1.5 rounded-lg transition-colors">عرض الكل &larr;</a>
          </div>
          
          <div className="flex gap-4 overflow-x-auto pb-4 custom-scrollbar snap-x">
            {reviews.map((review) => (
              <div key={review.id} className="min-w-[260px] max-w-[260px] sm:min-w-[300px] sm:max-w-[300px] bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden shadow-sm snap-start flex-shrink-0 flex flex-col">
                {review.review_type === 'video' && (
                  <div className="relative aspect-[9/16] bg-black h-48 sm:h-56">
                    <SmartVideoPlayer url={review.video_url} title={review.customer_name} />
                  </div>
                )}
                {review.review_type === 'image' && (
                  <div className="relative aspect-square bg-slate-100 dark:bg-slate-900 p-2 h-48 sm:h-56">
                    <img src={review.content} alt={review.customer_name} className="w-full h-full object-contain rounded-xl" />
                  </div>
                )}
                {review.review_type === 'text' && (
                  <div className="p-5 bg-gradient-to-b from-white to-slate-50 dark:from-slate-800 dark:to-slate-900 flex-1 h-48 sm:h-56 overflow-y-auto">
                    <SafeIcon icon={LuQuote} className="w-6 h-6 text-indigo-100 dark:text-indigo-900 mb-2" />
                    <p className="text-slate-700 dark:text-slate-300 font-medium text-sm italic">"{review.content}"</p>
                  </div>
                )}
                <div className="p-3 bg-slate-50 dark:bg-slate-900 border-t border-slate-100 dark:border-slate-700 mt-auto">
                  <h3 className="font-black text-slate-800 dark:text-white text-sm truncate">{review.customer_name}</h3>
                  {review.car_purchased && (
                    <p className="text-xs font-bold text-indigo-600 dark:text-indigo-400 truncate mt-0.5">{review.car_purchased}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};